/**
 * The screen shown when the app itself has broken, in place of everything.
 *
 * It follows the standard for a failure on our side (D31,
 * lib/serverFailure): the crash is already reported by Sentry's boundary, the
 * words say so and give the support address as a mailto link, and there is
 * no "Try again". Reload stays, as the way back into the app.
 *
 * Used by: main.tsx, as the fallback of the Sentry error boundary around the
 * whole app.
 *
 * @module CrashScreen
 */
import React from 'react';
import { SUPPORT_EMAIL } from '@/config/tunables/shared';
import { referenceCode } from '@/shared/lib/serverFailureCore';

/** The report's id. */
interface CrashScreenProps {
  /** Sentry's id for the report, when reporting is on. */
  eventId: string | null;
}

/**
 * What a gardener sees when the app itself has broken.
 *
 * This renders outside every provider, because it has to: the error it is
 * standing in for may have come from one of them. So there is no translation
 * hook to call. The two languages the app speaks are written out here, chosen
 * by the browser's own setting — crude next to the rest of the app, and the
 * right trade for a screen whose whole job is to exist when nothing else does.
 *
 * The reference (the first 8 characters of the report id, also the event's "reference" tag in Sentry, E3) is shown so that "it broke" in a text message can become a
 * specific event on the other end. The sentence is the standard one
 * (serverFailure.message in the dictionaries), written out here for the same
 * reason, split around the address so it can be a link.
 */
export const CrashScreen: React.FC<CrashScreenProps> = ({ eventId }) => {
  const es = typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('es');
  const copy = es
    ? {
        title: 'Algo salió mal',
        before: 'Algo salió mal de nuestro lado. Ya nos avisaron. Si necesitas ayuda, escríbenos a ',
        after: '.',
        reload: 'Recargar',
        ref: 'Referencia',
      }
    : {
        title: 'Something went wrong',
        before: "Something went wrong on our side. We've been told about it. If you need help, write to us at ",
        after: '.',
        reload: 'Reload',
        ref: 'Reference',
      };

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
      <div className="w-full max-w-sm space-y-4 text-center">
        <h1 className="text-xl font-semibold">{copy.title}</h1>
        <p className="text-sm text-muted-foreground">
          {copy.before}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="underline underline-offset-2 break-words">{SUPPORT_EMAIL}</a>
          {copy.after}
        </p>
        <div className="flex justify-center gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {copy.reload}
          </button>
        </div>
        {referenceCode(eventId) && (
          <p className="text-xs text-muted-foreground">
            {copy.ref}: <span className="select-text">{referenceCode(eventId)}</span>
          </p>
        )}
      </div>
    </main>
  );
};
